import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, API } from "../lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Send, MessageSquare, Plus, Paperclip, X, Megaphone } from "lucide-react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";

export default function Inbox() {
    const [threads, setThreads] = useState([]);
    const [numbers, setNumbers] = useState([]);
    const [activeThread, setActiveThread] = useState(null);
    const [activeNumberId, setActiveNumberId] = useState(null);
    const [messages, setMessages] = useState([]);
    const [draft, setDraft] = useState("");
    const [newPeer, setNewPeer] = useState("");
    const [showCompose, setShowCompose] = useState(false);
    const scrollRef = useRef(null);
    const fileInputRef = useRef(null);
    const [pendingMedia, setPendingMedia] = useState(null); // {path, blobUrl}
    const [uploading, setUploading] = useState(false);
    const [inboxAd, setInboxAd] = useState(null);
    const location = useLocation();

    const queryNumber = useMemo(() => new URLSearchParams(location.search).get("number"), [location.search]);

    const loadThreads = async () => {
        const { data } = await api.get("/sms/threads");
        setThreads(data);
    };

    const loadNumbers = async () => {
        const { data } = await api.get("/numbers/my");
        setNumbers(data);
        if (data.length > 0 && !activeNumberId) {
            setActiveNumberId(queryNumber || data[0].number_id);
        }
    };

    useEffect(() => {
        loadNumbers();
        loadThreads();
        (async () => {
            try {
                const { data } = await api.get("/ads/inbox");
                if (data.length) setInboxAd(data[Math.floor(Math.random() * data.length)]);
            } catch { /* ads are non-critical */ }
        })();
    }, []);

    useEffect(() => {
        if (!activeThread) return;
        const loadMsgs = async () => {
            const { data } = await api.get(
                `/sms/messages?peer=${encodeURIComponent(activeThread.peer_number)}&number_id=${activeThread.number_id}`
            );
            setMessages(data);
            setTimeout(() => scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight), 50);
        };
        loadMsgs();
        const id = setInterval(loadMsgs, 4000);
        return () => clearInterval(id);
    }, [activeThread]);

    const send = async (e) => {
        e?.preventDefault();
        if ((!draft.trim() && !pendingMedia) || !activeThread) return;
        try {
            await api.post("/sms/send", {
                from_number_id: activeThread.number_id,
                to_number: activeThread.peer_number,
                body: draft.trim(),
                media_paths: pendingMedia ? [pendingMedia.path] : undefined,
            });
            setDraft("");
            if (pendingMedia?.blobUrl) URL.revokeObjectURL(pendingMedia.blobUrl);
            setPendingMedia(null);
            const { data } = await api.get(
                `/sms/messages?peer=${encodeURIComponent(activeThread.peer_number)}&number_id=${activeThread.number_id}`
            );
            setMessages(data);
            loadThreads();
        } catch (e) {
            if (e?.response?.status === 402) {
                toast.error("Out of SMS credits. Upgrade your plan.");
            } else {
                toast.error("Send failed");
            }
        }
    };

    const onPickFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!file.type.startsWith("image/")) {
            toast.error("Only image attachments are supported");
            return;
        }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/sms/upload-media", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            const blobUrl = URL.createObjectURL(file);
            setPendingMedia({ path: data.path, blobUrl });
            toast.success("Image attached");
        } catch (e) {
            toast.error("Upload failed");
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    };

    const startConversation = async () => {
        if (!newPeer.trim() || !activeNumberId) {
            toast.error("Pick a sending number and enter recipient");
            return;
        }
        const ownNumber = numbers.find((n) => n.number_id === activeNumberId)?.phone_number;
        const newThread = {
            thread_id: `${activeNumberId}:${newPeer}`,
            number_id: activeNumberId,
            own_number: ownNumber,
            peer_number: newPeer.trim(),
            last_message: "",
            last_at: new Date().toISOString(),
        };
        setActiveThread(newThread);
        setMessages([]);
        setShowCompose(false);
        setNewPeer("");
    };

    return (
        <div className="space-y-6" data-testid="inbox-page">
            <div className="flex items-end justify-between">
                <div>
                    <p className="font-mono text-[11px] uppercase tracking-wider text-zinc-500 mb-2">— Messaging</p>
                    <h1 className="font-display text-4xl font-bold tracking-tight">Inbox</h1>
                </div>
                <Button
                    onClick={() => setShowCompose((v) => !v)}
                    data-testid="compose-toggle-btn"
                    className="bg-black text-white rounded-sm h-10"
                >
                    <Plus className="w-4 h-4 mr-1" /> New
                </Button>
            </div>

            {numbers.length === 0 && (
                <div className="border border-zinc-200 rounded-sm p-12 text-center bg-zinc-50/50">
                    <MessageSquare className="w-6 h-6 mx-auto text-accent-blue mb-3" />
                    <p className="font-display text-xl font-bold">Buy a number to start texting</p>
                </div>
            )}

            {showCompose && numbers.length > 0 && (
                <div className="border border-zinc-200 rounded-sm p-6 bg-zinc-50/50">
                    <div className="grid md:grid-cols-12 gap-3">
                        <div className="md:col-span-4">
                            <label className="font-mono text-[10px] uppercase text-zinc-500">From</label>
                            <select
                                value={activeNumberId || ""}
                                onChange={(e) => setActiveNumberId(e.target.value)}
                                data-testid="compose-from-select"
                                className="w-full mt-1 h-10 px-3 rounded-sm border border-zinc-300 font-mono text-sm bg-white"
                            >
                                {numbers.map((n) => (
                                    <option key={n.number_id} value={n.number_id}>
                                        {n.phone_number}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="md:col-span-6">
                            <label className="font-mono text-[10px] uppercase text-zinc-500">To</label>
                            <Input
                                value={newPeer}
                                onChange={(e) => setNewPeer(e.target.value)}
                                placeholder="+15551234567"
                                className="rounded-sm font-mono mt-1"
                                data-testid="compose-to-input"
                            />
                        </div>
                        <div className="md:col-span-2 flex items-end">
                            <Button onClick={startConversation} data-testid="compose-start-btn" className="w-full bg-accent-blue hover:bg-blue-600 text-white rounded-sm h-10">
                                Start
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            <div className="grid lg:grid-cols-12 gap-px bg-zinc-200 border border-zinc-200 min-h-[560px]">
                {/* Thread list */}
                <aside className="lg:col-span-4 bg-white">
                    <div className="px-4 py-3 border-b border-zinc-200">
                        <h3 className="font-mono text-[11px] uppercase tracking-wider text-zinc-500">Conversations</h3>
                    </div>
                    <ul className="divide-y divide-zinc-200 max-h-[560px] overflow-y-auto">
                        {inboxAd && <InboxAd ad={inboxAd} />}
                        {threads.length === 0 && (
                            <li className="p-6 text-center text-sm text-zinc-500">No conversations yet</li>
                        )}
                        {threads.map((t) => (
                            <li key={t.thread_id}>
                                <button
                                    onClick={() => setActiveThread(t)}
                                    data-testid={`thread-${t.thread_id}`}
                                    className={`w-full text-left px-4 py-3 hover:bg-zinc-50 transition-colors ${
                                        activeThread?.thread_id === t.thread_id ? "bg-zinc-100" : ""
                                    }`}
                                >
                                    <div className="flex items-center justify-between">
                                        <span className="font-mono text-sm">{t.peer_number}</span>
                                        <span className="font-mono text-[10px] text-zinc-400">{relative(t.last_at)}</span>
                                    </div>
                                    <p className="text-xs text-zinc-500 mt-1 truncate">{t.last_message}</p>
                                </button>
                            </li>
                        ))}
                    </ul>
                </aside>

                {/* Thread view */}
                <section className="lg:col-span-8 bg-zinc-50/50 flex flex-col">
                    {!activeThread && (
                        <div className="flex-1 flex items-center justify-center text-sm text-zinc-500">
                            Select a conversation to start chatting.
                        </div>
                    )}
                    {activeThread && (
                        <>
                            <header className="px-5 py-3 border-b border-zinc-200 bg-white flex items-center justify-between">
                                <div>
                                    <div className="font-mono text-sm">{activeThread.peer_number}</div>
                                    <div className="font-mono text-[10px] text-zinc-500 uppercase">
                                        via {activeThread.own_number}
                                    </div>
                                </div>
                            </header>
                            <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-2 max-h-[440px]">
                                {messages.map((m) => (
                                    <div
                                        key={m.message_id}
                                        className={`flex ${m.direction === "outbound" ? "justify-end" : "justify-start"}`}
                                    >
                                        <div
                                            className={`max-w-[70%] px-3 py-2 rounded-sm border ${
                                                m.direction === "outbound"
                                                    ? "bg-espresso text-[#FAF5FF] border-espresso"
                                                    : "bg-paper border-[#DDD6FE]"
                                            }`}
                                        >
                                            {Array.isArray(m.media) && m.media.length > 0 && (
                                                <div className="mb-1.5 space-y-1.5">
                                                    {m.media.map((p) => (
                                                        <MediaAuthImg key={p} path={p} />
                                                    ))}
                                                </div>
                                            )}
                                            {m.body && (
                                                <div className="text-sm leading-snug whitespace-pre-wrap">{m.body}</div>
                                            )}
                                            <div className={`font-mono text-[10px] mt-1 ${
                                                m.direction === "outbound" ? "text-zinc-400" : "text-zinc-500"
                                            }`}>
                                                {new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} · {m.status}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <form onSubmit={send} className="border-t border-[#DDD6FE] bg-paper p-3">
                                {pendingMedia && (
                                    <div className="mb-2 inline-flex items-center gap-2 px-2 py-1 border border-[#DDD6FE] rounded-sm bg-cream/40">
                                        <img src={pendingMedia.blobUrl} alt="" className="w-10 h-10 object-cover rounded-sm" />
                                        <span className="font-mono text-xs text-zinc-600 truncate max-w-[180px]">{pendingMedia.path.split("/").pop()}</span>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                if (pendingMedia.blobUrl) URL.revokeObjectURL(pendingMedia.blobUrl);
                                                setPendingMedia(null);
                                            }}
                                            data-testid="remove-attachment-btn"
                                            className="text-zinc-500 hover:text-red-600"
                                        >
                                            <X className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                )}
                                <div className="flex items-center gap-2">
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept="image/*"
                                        onChange={onPickFile}
                                        className="hidden"
                                        data-testid="mms-file-input"
                                    />
                                    <Button
                                        type="button"
                                        onClick={() => fileInputRef.current?.click()}
                                        disabled={uploading}
                                        data-testid="attach-mms-btn"
                                        variant="outline"
                                        className="rounded-sm h-10 px-3 border-[#DDD6FE]"
                                    >
                                        <Paperclip className="w-4 h-4" />
                                    </Button>
                                    <Input
                                        value={draft}
                                        onChange={(e) => setDraft(e.target.value)}
                                        placeholder="Type a message…"
                                        className="rounded-sm font-mono"
                                        data-testid="message-input"
                                    />
                                    <Button
                                        type="submit"
                                        disabled={!draft.trim() && !pendingMedia}
                                        data-testid="send-sms-btn"
                                        className="bg-espresso text-[#FAF5FF] rounded-sm h-10 px-4"
                                    >
                                        <Send className="w-4 h-4" />
                                    </Button>
                                </div>
                            </form>
                        </>
                    )}
                </section>
            </div>
        </div>
    );
}

function InboxAd({ ad }) {
    const onClick = () => {
        if (ad.campaign_id) api.post(`/ads/${ad.campaign_id}/click`).catch(() => {});
    };
    return (
        <li className="bg-[#F3E8FF]/40" data-testid="inbox-sponsored-ad">
            <a href={ad.cta_url} target="_blank" rel="noopener noreferrer sponsored" onClick={onClick}
                className="block px-4 py-3 hover:bg-[#F3E8FF]/70 transition-colors" data-testid="inbox-sponsored-cta">
                <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-[#4C1D95] flex items-center gap-1.5">
                        <Megaphone className="w-3.5 h-3.5" /> {ad.business_name}
                    </span>
                    <span className="font-mono text-[9px] uppercase tracking-wider text-zinc-400 border border-[#DDD6FE] rounded-sm px-1 py-0.5">Sponsored</span>
                </div>
                <p className="text-xs text-zinc-600 mt-1 truncate">{ad.headline} — {ad.body}</p>
            </a>
        </li>
    );
}

function relative(iso) {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return "now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    return `${Math.floor(diff / 86400)}d`;
}

function MediaAuthImg({ path }) {
    const [src, setSrc] = useState(null);
    useEffect(() => {
        let active = true;
        let blob;
        api.get(`/media/${path}`, { responseType: "blob" }).then((r) => {
            if (!active) return;
            blob = URL.createObjectURL(r.data);
            setSrc(blob);
        }).catch(() => {});
        return () => {
            active = false;
            if (blob) URL.revokeObjectURL(blob);
        };
    }, [path]);
    if (!src) return <div className="w-40 h-40 bg-cream/40 rounded-sm border border-[#DDD6FE] animate-pulse" />;
    return <img src={src} alt="" className="max-w-[240px] rounded-sm border border-[#DDD6FE]" />;
}
